/**
 * Formato de la línea de crédito v2 (FINA-3378). Vive en un util puro para que los utils, los
 * componentes nuevos y los modales lo importen sin depender de `credit-line.component`
 * (que lo re-exporta por compatibilidad).
 */

/** Fecha corta (dd/mm/aaaa) en Caracas: la línea cuenta en días civiles de Venezuela. */
export const formatLineDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Caracas' });
};

/** Monto en USD sin el símbolo: miles con punto, decimales solo si los hay («1.250», «412,5»). */
export const formatLineUsd = (n: number | null | undefined): string => {
    const v = Number(n) || 0;
    return v.toLocaleString('es-VE', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
};

/** Monto en USD sin el símbolo, siempre con 2 decimales («275,00», «1.375,00»): total, cuotas e interés del diseño. */
export const formatLineMoney = (n: number | null | undefined): string => {
    const v = Number(n) || 0;
    return v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ─── Días calendario de Caracas (fechas de cuota) ────────────────────────────────────────────
// Un día calendario viaja como `YYYY-MM-DD` y nunca pasa por `new Date('YYYY-MM-DD')` local:
// `formatLineDate` hace `new Date(iso)` y un `YYYY-MM-DD` (medianoche UTC) cae un día antes en
// Caracas. Para fechas de cuota se usa la cadena caracasTodayIso → addDaysIso → formatLineDay.

/** `en-CA` formatea como `YYYY-MM-DD`. Hoisteado: crear un `Intl.DateTimeFormat` es caro. */
const CARACAS_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Día calendario de Caracas (`YYYY-MM-DD`) del instante dado. */
export const caracasTodayIso = (now: Date = new Date()): string => CARACAS_DAY.format(now);

/** Suma `n` días a un día calendario `YYYY-MM-DD`, en UTC para no depender de la zona del navegador. */
export const addDaysIso = (iso: string, n: number): string => {
    const [y, m, d] = iso.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + (Number(n) || 0)));
    const pad = (v: number): string => String(v).padStart(2, '0');
    return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};

/** `YYYY-MM-DD` → `dd/mm/aaaa` por split, sin `Date`. Mismo formato visible que `formatLineDate`. */
export const formatLineDay = (iso: string): string => {
    const [y, m, d] = (iso || '').split('-');
    return y && m && d ? `${d}/${m}/${y}` : '';
};

const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** `YYYY-MM-DD` → «31 de octubre» por split, sin `Date` ni año (Resumen y términos de la solicitud). */
export const formatLineDayLong = (iso: string): string => {
    const [y, m, d] = (iso || '').split('-').map(Number);
    return y && m && d && MONTHS_ES[m - 1] ? `${d} de ${MONTHS_ES[m - 1]}` : '';
};

/** `YYYY-MM-DD` → `dd/mm` por split, sin año (tarjetas «Vence el» de la calculadora). */
export const formatLineDayShort = (iso: string): string => {
    const [y, m, d] = (iso || '').split('-');
    return y && m && d ? `${d}/${m}` : '';
};
